import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPasswordSubtitle } from '@lib/components/2fa';
import { Values } from '@lib/interfaces/form';
import { FetchResult } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql';
import { AuthenticationPasswordsForgotMutation } from 'graphql-common';
import Typography from '@lib/components/Typography/Typography';
import Form from '@lib/components/ReactHookForm/FormContainer';
import Modal from '@lib/components/Modal/Modal';
import { IconName } from '@lib/components/Modal/enums';
import { Colors } from '@lib/enums/colors';
import { SecurityCard } from '@lib/components/SecurityCard';
import getPasswordFormFields from '../utils/getPasswordFormFields';
import PasswordFormActions from './PasswordFormActions';
import { OnUpdatePassword } from '../types';
import styles from './ProfileForm.module.scss';

interface Props {
  graphQLErrors?: readonly GraphQLFormattedError[];
  loading: boolean;
  onSendPasswordResetHandler: () => Promise<
    FetchResult<AuthenticationPasswordsForgotMutation>
  >;
  onSubmit: OnUpdatePassword;
  passwordUpdatedAt?: string | null;
  updateMutationReset: () => void;
}

const defaultValues = {
  passwordChallenge: '',
  password: '',
  passwordConfirmation: '',
};

function PasswordForm(props: Props) {
  const {
    graphQLErrors,
    onSendPasswordResetHandler,
    onSubmit,
    loading,
    passwordUpdatedAt,
    updateMutationReset,
  } = props;
  const [isChangePasswordModalOpened, setChangePasswordModalOpened] =
    useState(false);
  const { t } = useTranslation();

  const openChangePasswordModal = () => setChangePasswordModalOpened(true);
  const closeChangePasswordModal = useCallback(() => {
    setChangePasswordModalOpened(false);
  }, []);

  const fields = getPasswordFormFields({
    t,
    onSendPasswordResetHandler,
    closeChangePasswordModal,
  });

  const onSubmitHandler = (values: Values) => {
    onSubmit(values).then(({ errors }) => {
      if (!errors) {
        closeChangePasswordModal();
      }
    });
  };

  useEffect(() => {
    if (!isChangePasswordModalOpened) {
      updateMutationReset();
    }
  }, [updateMutationReset, isChangePasswordModalOpened]);

  return (
    <>
      <div className={styles.securitySection}>
        <Typography variant="h3" className={styles.securityHeading}>
          {t('security-and-password')}
        </Typography>
        <SecurityCard
          icon="key"
          iconColor={Colors.Blue}
          title={t('password')}
          subtitle={getPasswordSubtitle(passwordUpdatedAt, t)}
          buttonText={t('change-password')}
          onButtonClick={openChangePasswordModal}
        />
      </div>
      <Modal
        isOpen={isChangePasswordModalOpened}
        title={t('change-password')}
        icon={IconName.ChangePassword}
        onClose={closeChangePasswordModal}
      >
        <Form
          actionsComponent={PasswordFormActions}
          actionsComponentProps={{
            closeChangePasswordModal,
          }}
          defaultValues={defaultValues}
          fields={fields}
          formClassName={styles.form}
          graphQLErrors={graphQLErrors}
          loading={loading}
          onSubmit={onSubmitHandler}
        />
      </Modal>
    </>
  );
}

export default PasswordForm;
